import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth data on mount
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('authUser');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    setToken(token);
    setUser(userData);
    localStorage.setItem('authToken', token);
    localStorage.setItem('authUser', JSON.stringify(userData));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
  };

  const updateUser = (partial) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      localStorage.setItem('authUser', JSON.stringify(next));
      return next;
    });
  };

  const isAdmin = () => {
    return user?.roles && Array.isArray(user.roles) && user.roles.includes('ADMIN');
  };

  const isApprovedCreator = () => {
    return user?.isApprovedCreator === true;
  };

  const isClubAdmin = () => {
    return user?.roles && Array.isArray(user.roles) && user.roles.includes('CLUB_ADMIN');
  };

  const isClubCreator = () => {
    return user?.roles && Array.isArray(user.roles) && user.roles.includes('CLUB_CREATOR');
  };

  // Note: Per-club coordinator roles would need to be checked via API calls
  // as they are stored in ClubMember, not in User
  const isMatchCoordinator = async (clubId) => {
    // This would require an API call to check club membership and roles
    // For now, return false - can be implemented later with API integration
    return false;
  };

  const isPracticeCoordinator = async (clubId) => {
    // This would require an API call to check club membership and roles
    // For now, return false - can be implemented later with API integration
    return false;
  };

  const value = {
    user,
    token,
    login,
    logout,
    isAdmin,
    isApprovedCreator,
    isClubAdmin,
    isClubCreator,
    isMatchCoordinator,
    isPracticeCoordinator,
    isAuthenticated: !!user && !!token,
    loading,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

